import type { TicketDetail, TicketDetailArtifact } from '@nightshift/ui-types';

/**
 * The refiner's `obsolete-claim` artifact as the drawer reads it. The orchestrator validated it
 * when it was stored (`core/src/pipeline/obsolete.ts`); this only reads the fields it renders
 * and drops anything malformed rather than guessing.
 */
export interface ObsoleteClaimView {
  kind: 'already-done' | 'no-longer-applies';
  confidence: 'high' | 'medium';
  reason: string;
  evidence: Array<{ path: string; line?: number; observation: string }>;
  resolvedBy?: string;
  harness: string | null;
}

export const OBSOLETE_KIND_LABEL: Record<ObsoleteClaimView['kind'], string> = {
  'already-done': 'Already done',
  'no-longer-applies': 'No longer applies',
};

/** The newest claim on the ticket: the one the open proposal was raised from. */
export function currentObsoleteClaimArtifact(detail: Pick<TicketDetail, 'artifacts'>): TicketDetailArtifact | null {
  return [...detail.artifacts].reverse().find((row) => row.kind === 'obsolete-claim') ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseObsoleteClaimText(text: string): ObsoleteClaimView | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const { kind, confidence, reason, evidence, resolvedBy, harness } = parsed;
  if (kind !== 'already-done' && kind !== 'no-longer-applies') return null;
  if (confidence !== 'high' && confidence !== 'medium') return null;
  if (typeof reason !== 'string' || !Array.isArray(evidence)) return null;
  const rows: ObsoleteClaimView['evidence'] = [];
  for (const entry of evidence) {
    if (!isRecord(entry) || typeof entry.path !== 'string' || typeof entry.observation !== 'string') return null;
    rows.push({
      path: entry.path,
      ...(typeof entry.line === 'number' ? { line: entry.line } : {}),
      observation: entry.observation,
    });
  }
  return {
    kind,
    confidence,
    reason,
    evidence: rows,
    ...(typeof resolvedBy === 'string' && resolvedBy.length > 0 ? { resolvedBy } : {}),
    harness: typeof harness === 'string' ? harness : null,
  };
}

/** The independent check's result, when a project's policy sent the claim to one. */
export interface ObsoleteCheckView {
  verdict: 'obsolete-confirmed' | 'obsolete-disputed';
  reason: string;
  evidence: ObsoleteClaimView['evidence'];
  harness: string | null;
}

/** The newest check that came after the current claim; an older one answered an older claim. */
export function currentObsoleteCheckArtifact(detail: Pick<TicketDetail, 'artifacts'>): TicketDetailArtifact | null {
  const claim = currentObsoleteClaimArtifact(detail);
  if (!claim) return null;
  return [...detail.artifacts].reverse().find((row) => row.kind === 'obsolete-check' && row.id > claim.id) ?? null;
}

export function parseObsoleteCheckText(text: string): ObsoleteCheckView | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const { verdict, reason, evidence, harness } = parsed;
  if (verdict !== 'obsolete-confirmed' && verdict !== 'obsolete-disputed') return null;
  if (typeof reason !== 'string' || !Array.isArray(evidence)) return null;
  const rows: ObsoleteCheckView['evidence'] = [];
  for (const entry of evidence) {
    if (!isRecord(entry) || typeof entry.path !== 'string' || typeof entry.observation !== 'string') return null;
    rows.push({ path: entry.path, ...(typeof entry.line === 'number' ? { line: entry.line } : {}), observation: entry.observation });
  }
  return { verdict, reason, evidence: rows, harness: typeof harness === 'string' ? harness : null };
}
